import { z } from 'zod'
import { utcDateTimeString } from '../validation'
import { dropCardDtoSchema } from './drop'
import { ITEM_OCCASIONS } from './enrichment'
import { listingActionCardDtoSchema } from './listing-action'
import { liveMapFaceSchema } from './live-room'
import { userRoomVisibilityValues } from './live-room-user'

const coordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
})

const feedLocationSchema = z.object({
  name: z.string().nullable(),
  street: z.string(),
  city: z.string(),
  country: z.string(),
  coordinates: coordinatesSchema,
})

const eventPriceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unknown') }),
  z.object({ kind: z.literal('free') }),
  z.object({ kind: z.literal('fixed'), amount: z.string() }),
  z.object({ kind: z.literal('range'), min: z.string(), max: z.string() }),
])

const viewerEventInterestValues = ['interested', 'hidden'] as const

export type ViewerEventInterest = (typeof viewerEventInterestValues)[number]

const friendInterestPreviewSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
})

export type FriendInterestPreview = z.infer<typeof friendInterestPreviewSchema>

const landingListingItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  location: feedLocationSchema,
  coverImageUrl: z.string(),
  distance: z.number(),
  rating: z.number(),
  interestedCount: z.number(),
  friendInterestCount: z.number(),
  friendInterestPreviews: z.array(friendInterestPreviewSchema),
})

const landingEventItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  location: feedLocationSchema,
  coverImageUrl: z.string(),
  distance: z.number(),
  startTime: z.string(),
  endTime: z.string(),
  price: eventPriceSchema,
  listingId: z.string().nullable(),
  interestedCount: z.number(),
  /** Live anonymous presence count at the event's materialized place; optional
   * so a not-yet-deployed backend still parses. */
  presentTotal: z.number().optional(),
  friendInterestCount: z.number(),
  friendInterestPreviews: z.array(friendInterestPreviewSchema),
  /** True when the viewer follows the organizer, its channel or the listing the event is at;
   * attached per-request on top of the anonymous snapshot, absent for guests. */
  followed: z.boolean().optional(),
  /** The youngest age allowed in (16 or 18); absent for events open to everyone. */
  minAge: z.number().int().optional(),
  /** Only students get in; absent when the event is open to everyone. */
  studentsOnly: z.boolean().optional(),
  /** Only the organizer's members see it; set on the members' own injected events. */
  membersOnly: z.boolean().optional(),
  /** The venue is announced later: show "Ort folgt" instead of a place and a distance. */
  locationPending: z.boolean().optional(),
  /** Only guests see the address: the card shows the city, never a place or a distance. */
  addressHidden: z.boolean().optional(),
  /** The signed-in viewer's own mark: `interested` when friends may see it, `hidden` when saved
   * just for themselves, null without one; attached per request, absent for guests. */
  viewerInterest: z.enum(viewerEventInterestValues).nullable().optional(),
})

/** A running Live Room someone is in, for the For You feed. */
const landingRoomItemSchema = z.object({
  /** The event behind the room, as the room screen takes it. */
  id: z.string().uuid(),
  title: z.string(),
  coverUrl: z.string().nullable(),
  /** People inside right now, never 0. */
  here: z.number().int().positive(),
  /** Friends inside first, then people who chose to be seen by everyone. */
  faces: z.array(liveMapFaceSchema).max(5),
  visibility: z.enum(userRoomVisibilityValues),
  /** Meters from the feed's coordinates. */
  distance: z.number(),
  friendInside: z.boolean(),
  venueRoom: z.boolean(),
  endsAt: z.string(),
})

export type LandingRoomItem = z.infer<typeof landingRoomItemSchema>

export const landingFeedItemSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('listing'), item: landingListingItemSchema }),
  z.object({ type: z.literal('event'), item: landingEventItemSchema }),
  // Campaign row spliced server-side into landing page 1 — never part of the
  // anonymous snapshot or the pagination math.
  z.object({ type: z.literal('drop'), item: dropCardDtoSchema }),
  // A place's running action, spliced per request into landing page 1 like the drop row.
  z.object({ type: z.literal('action'), item: listingActionCardDtoSchema }),
  // Only for apps that ask with include: ['live_rooms']; spliced per request like the action row.
  z.object({ type: z.literal('room'), item: landingRoomItemSchema }),
])

export const landingFeedRequestSchema = z.object({
  coordinates: coordinatesSchema,
  dateTime: utcDateTimeString,
  type: z.enum(['listing', 'event', 'all']).optional(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().max(100).optional(),
  /** Row kinds beyond places, events and drops that the app can draw; older apps send none. */
  include: z.array(z.enum(['action', 'daily_drop', 'live_rooms'])).optional(),
  /** `now`: only events running or starting within the hour, the busiest and nearest first. */
  mode: z.enum(['now']).optional(),
  /** Only events for students (student ID checked at the door); places drop out. */
  studentsOnly: z.boolean().optional(),
  /** Only events and places sorted as good for this occasion, the best fitting a little ahead. */
  occasion: z.enum(ITEM_OCCASIONS).optional(),
  /**
   * One of the viewer's own explore modes: its go-to places near the coordinates first, then
   * what fits the mode. Ranks only; ignored for guests and for modes that are not the viewer's.
   */
  modeId: z.string().uuid().optional(),
})

export const pulseFeedRequestSchema = z.object({
  coordinates: coordinatesSchema,
  dateTime: utcDateTimeString.optional(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().max(100).optional(),
  tagIds: z.array(z.string().uuid()).optional(),
})

const areaSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  // omit radiusKm to rank by proximity without a max-range filter
  radiusKm: z.number().positive().optional(),
})

const dateRangeSchema = z
  .object({
    startDateTime: utcDateTimeString,
    endDateTime: utcDateTimeString,
  })
  .refine((v) => new Date(v.startDateTime) < new Date(v.endDateTime), {
    message: 'startDateTime must be before endDateTime',
    path: ['startDateTime'],
  })

export const searchFeedRequestSchema = z.object({
  query: z.string().min(2).optional(),
  area: areaSchema.optional(),
  dateRange: dateRangeSchema.optional(),
  type: z.enum(['listing', 'event', 'all']).optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  /** Only events for students (student ID checked at the door); places drop out. */
  studentsOnly: z.boolean().optional(),
  /**
   * `good` ranks by what is good right now (time, distance, popularity); `relevance` by keyword
   * score. Omitted: `relevance` with a query, `good` without one.
   */
  sort: z.enum(['relevance', 'good']).optional(),
  /** Only events known to be free; an unknown price is not free, and places drop out. */
  freeOnly: z.boolean().optional(),
  /** Only places open right now per their opening hours (Vienna time); events are unaffected. */
  openNow: z.boolean().optional(),
  /** Only events and places sorted as good for this occasion, the best fitting a little ahead. */
  occasion: z.enum(ITEM_OCCASIONS).optional(),
  /**
   * One of the viewer's own explore modes: what fits the mode and its go-to places rank higher.
   * Ranks only; ignored for guests and for modes that are not the viewer's.
   */
  modeId: z.string().uuid().optional(),
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().max(100).optional(),
})

export const feedPaginationSchema = z.object({
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
})

export const feedPaginatedResponseSchema = z.object({
  data: z.array(landingFeedItemSchema),
  pagination: feedPaginationSchema,
})

/** Words of a search query the server read as filters and took out of the keyword search. */
export const searchAppliedHintsSchema = z.object({
  date: z.enum(['today', 'tomorrow', 'weekend']).optional(),
  tagNames: z.array(z.string()).optional(),
  freeOnly: z.boolean().optional(),
})

export const searchFeedResponseSchema = feedPaginatedResponseSchema.extend({
  appliedHints: searchAppliedHintsSchema.optional(),
})

/** GET /api/feed/discovery — one request replaces eight parallel searches. */
export const discoveryFeedQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  /** Comma-separated category root tag UUIDs. Omitted means no category filter, as in search. */
  tagIds: z
    .string()
    .optional()
    .transform((value, ctx) => {
      if (value == null || value.trim() === '') return undefined
      const ids = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      for (const id of ids) {
        if (!z.string().uuid().safeParse(id).success) {
          ctx.addIssue({ code: 'custom', message: `Invalid tag id: ${id}`, path: ['tagIds'] })
          return z.NEVER
        }
      }
      return ids.slice(0, 20)
    }),
})

export const discoveryFeedSectionSchema = z.object({
  key: z.string(),
  radiusKm: z.number(),
  type: z.enum(['event']).optional(),
  items: z.array(landingFeedItemSchema),
})

export const discoveryFeedResponseSchema = z.object({
  sections: z.array(discoveryFeedSectionSchema),
})

export type LandingFeedItem = z.infer<typeof landingFeedItemSchema>
export type LandingFeedRequest = z.infer<typeof landingFeedRequestSchema>
/** Landing feed type filter; the single source for backend and app alike. */
export type LandingFeedType = NonNullable<LandingFeedRequest['type']>
export type PulseFeedRequest = z.infer<typeof pulseFeedRequestSchema>
export type SearchFeedRequest = z.infer<typeof searchFeedRequestSchema>
export type SearchAppliedHints = z.infer<typeof searchAppliedHintsSchema>
export type FeedPaginatedResponse = z.infer<typeof feedPaginatedResponseSchema>
export type DiscoveryFeedQuery = z.infer<typeof discoveryFeedQuerySchema>
export type DiscoveryFeedResponse = z.infer<typeof discoveryFeedResponseSchema>
